export type SocialNetworkId = 'vk' | 'max';

export type ExpertiseLevel = 'beginner' | 'experienced' | 'advanced';

export type ExpertiseRow = {
  id: string;
  profession: string;
  level: ExpertiseLevel;
  testAssignment: string;
};

export type WorkHistoryRow = {
  id: string;
  workplace: string;
  position: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
};

export type ProfileExtras = {
  specialty: string;
  gradYear: string;
  primaryDirection: string;
  directions: string[];
  workHistory: WorkHistoryRow[];
  expertiseRows: ExpertiseRow[];
  socials: Partial<Record<SocialNetworkId, string>>;
};

const PROFILE_EXTRAS_KEY = 'sciplex_profile_extras';

export function createExpertiseRow(): ExpertiseRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    profession: '',
    level: 'advanced',
    testAssignment: '',
  };
}

export function createWorkHistoryRow(): WorkHistoryRow {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    workplace: '',
    position: '',
    startDate: '',
    endDate: '',
    isCurrent: false,
  };
}

const EMPTY_EXTRAS: ProfileExtras = {
  specialty: '',
  gradYear: '',
  primaryDirection: '',
  directions: [],
  workHistory: [createWorkHistoryRow()],
  expertiseRows: [createExpertiseRow()],
  socials: {},
};

function extrasStorageKey(userId: number) {
  return `${PROFILE_EXTRAS_KEY}_${userId}`;
}

function normalizeExpertiseLevel(raw: unknown): ExpertiseLevel {
  if (raw === 'beginner' || raw === 'advanced') return raw;
  return 'experienced';
}

function parseExpertiseRows(raw: unknown): ExpertiseRow[] {
  if (!Array.isArray(raw) || raw.length === 0) return [createExpertiseRow()];
  const rows = raw
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Partial<ExpertiseRow>;
      const profession = typeof row.profession === 'string' ? row.profession : '';
      const testAssignment = typeof row.testAssignment === 'string' ? row.testAssignment : '';
      const id = typeof row.id === 'string' && row.id.trim() ? row.id : `legacy-${index}`;
      return {
        id,
        profession,
        level: normalizeExpertiseLevel(row.level),
        testAssignment,
      } satisfies ExpertiseRow;
    })
    .filter((row): row is ExpertiseRow => row !== null);
  return rows.length > 0 ? rows : [createExpertiseRow()];
}

function parseWorkHistoryRows(raw: unknown, legacyExperience?: string): WorkHistoryRow[] {
  if (Array.isArray(raw) && raw.length > 0) {
    const rows = raw
      .map((item, index) => {
        if (!item || typeof item !== 'object') return null;
        const row = item as Partial<WorkHistoryRow>;
        const id = typeof row.id === 'string' && row.id.trim() ? row.id : `work-${index}`;
        return {
          id,
          workplace: typeof row.workplace === 'string' ? row.workplace : '',
          position: typeof row.position === 'string' ? row.position : '',
          startDate: typeof row.startDate === 'string' ? row.startDate : '',
          endDate: typeof row.endDate === 'string' ? row.endDate : '',
          isCurrent: row.isCurrent === true,
        } satisfies WorkHistoryRow;
      })
      .filter((row): row is WorkHistoryRow => row !== null);
    if (rows.length > 0) return rows;
  }

  if (typeof legacyExperience === 'string' && legacyExperience.trim()) {
    return [{ ...createWorkHistoryRow(), id: 'legacy-work' }];
  }

  return [createWorkHistoryRow()];
}

export function loadProfileExtras(userId: number): ProfileExtras {
  try {
    const raw = localStorage.getItem(extrasStorageKey(userId));
    if (!raw) {
      return { ...EMPTY_EXTRAS, socials: {}, directions: [], workHistory: [createWorkHistoryRow()], expertiseRows: [createExpertiseRow()] };
    }
    const parsed = JSON.parse(raw) as Partial<ProfileExtras> & { workExperience?: string };
    return {
      specialty: typeof parsed.specialty === 'string' ? parsed.specialty : '',
      gradYear: typeof parsed.gradYear === 'string' ? parsed.gradYear : '',
      primaryDirection: '',
      directions: [],
      workHistory: parseWorkHistoryRows(parsed.workHistory, parsed.workExperience),
      expertiseRows: parseExpertiseRows(parsed.expertiseRows),
      socials: {
        ...(typeof parsed.socials?.vk === 'string' ? { vk: parsed.socials.vk } : {}),
        ...(typeof parsed.socials?.max === 'string' ? { max: parsed.socials.max } : {}),
      },
    };
  } catch {
    return { ...EMPTY_EXTRAS, socials: {}, directions: [], workHistory: [createWorkHistoryRow()], expertiseRows: [createExpertiseRow()] };
  }
}

export function saveProfileExtras(userId: number, extras: ProfileExtras) {
  localStorage.setItem(extrasStorageKey(userId), JSON.stringify(extras));
}
